import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { guidFor } from '@ember/object/internals'
import { isEmpty } from '@ember/utils'
import { service } from '@ember/service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { and } from 'ember-truth-helpers'
import { fn } from '@ember/helper'

const ESCAPE = 'Escape'

/**
 * This component renders an input field and triggers actions for when the input value
 * changes, or an autocomplete option is selected.
 *
 * To enable the autocomplete features, set autocomplete = true and pass in options
 * as autocompleteOptions. The options should be an array of objects with a title
 * property defined for each object.
 *
 */

interface HasTitle {
  title: string
}

interface Args<Option> {
  updateAction?: (newValue?: string | null) => void
  selectOptionAction?: (option: Option) => void
  autocomplete?: boolean
  autocompleteOptions?: Option[] | null
  value?: Nullable<string>
  showOptionsWhenEmptyValue?: boolean
  placeholder?: string
}

interface Signature<Option extends HasTitle> {
  Element: HTMLDivElement
  Args: Args<Option>
  Blocks: {
    default: [Option]
  }
}

export class SearchBox<Option extends HasTitle> extends Component<Signature<Option>> {
  @service subscriptionType!: SubscriptionTypeService

  @tracked
  isExpanded = false

  get elementId() {
    return guidFor(this)
  }

  get themeClass() {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return 'hover:bg-oceany-blue-100 focus:bg-oceany-blue-100'
      case SubscriptionType.Maths:
        return 'hover:bg-ms-green-100 focus:bg-ms-green-100'
      default:
        return 'hover:bg-dusty-black-100 focus:bg-dusty-black-100'
    }
  }

  @action
  updateContents(newValue: string | null) {
    this.args.updateAction?.(newValue)
    this.showListIfValue()
  }

  @action
  onKey(e: KeyboardEvent) {
    if (e?.key === ESCAPE) this.updateContents(null)
  }

  /**
   * The action that is triggered when an autocomplete list option is selected. It sets the selected
   * option title as the search box value and hides the list.
   */
  @action
  selectOption(option: Option) {
    this.args.updateAction?.(option.title)
    this.args.selectOptionAction?.(option)
    this.hideList()
  }

  @action
  hideList() {
    this.isExpanded = false
  }

  /**
   * Only show autocomplete options when there's a text value, or component was told to show them anyway
   */
  @action
  showListIfValue() {
    if (this.args.autocomplete && (!isEmpty(this.args.value) || this.args.showOptionsWhenEmptyValue)) {
      this.isExpanded = true
    } else {
      this.isExpanded = false
    }
  }

  <template>
    <div
      data-test-search-box
      class="relative"
      aria-expanded={{this.isExpanded}}
      role={{if @autocomplete "combobox"}}
      ...attributes
    >
      <InputText
        data-test-search-box-input
        type="search"
        role="searchbox"
        class="w-full"
        value={{@value}}
        placeholder={{@placeholder}}
        aria-label={{t "components.searchBox.inputAriaLabel"}}
        aria-controls="{{this.elementId}}-listbox"
        aria-autocomplete={{if @autocomplete "list" "none"}}
        @themeKey={{themeKey this.subscriptionType.current}}
        @icon="search"
        @iconEnd={{true}}
        {{on "input" (eventValue this.updateContents)}}
        {{on "focusin" this.showListIfValue}}
        {{on "focusout" this.hideList}}
        {{! Only keydown seems to detect escape key, likely due to the focus bindings }}
        {{! template-lint-disable no-pointer-down-event-binding }}
        {{on "keydown" this.onKey}}
      />
      {{#if (and @autocomplete @autocompleteOptions)}}
        <ul
          data-test-search-box-autocomplete-list
          id="{{this.elementId}}-listbox"
          class="z-dropdown border-dusty-black-100 absolute max-h-52 w-full overflow-y-scroll rounded-md border bg-white
            {{unless this.isExpanded 'hidden'}}"
          role="listbox"
          aria-label={{t "components.searchBox.listAriaLabel"}}
        >
          {{#each @autocompleteOptions as |opt|}}
            {{! Unfortunately, due to the focusout action binding above, which hides the list, this needs to be bound to mousedown (or refactor the components) }}
            {{! template-lint-disable no-pointer-down-event-binding }}
            <li
              data-test-search-box-option
              {{on "mousedown" (fn this.selectOption opt)}}
              role="option"
              aria-selected="false"
              class="{{this.themeClass}} border-dusty-black-100 w-full cursor-pointer border-t p-3 text-sm font-bold"
            >
              {{#if (has-block)}}
                {{yield opt}}
              {{else}}
                {{opt.title}}
              {{/if}}
            </li>
          {{/each}}
        </ul>
      {{/if}}
    </div>
  </template>
}

export default SearchBox
